import { delJson, getJson, postJson, trimBase } from "./http.js";

/** Public user shape returned by the server (never includes the password hash). */
export interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  /** Whether the email address has been confirmed (older servers omit it). */
  email_verified?: boolean;
}

/** A signup/login/refresh result: the user plus a token pair. */
export interface AuthTokens {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

/**
 * Pure transport for the account endpoints (`/auth/*`). Holds no state — the
 * caller (web `auth.store`) owns token storage. Errors surface as {@link SyncError}.
 */
export class AuthClient {
  constructor(private readonly baseUrl: string) {}

  signup(email: string, password: string): Promise<AuthTokens> {
    return postJson<AuthTokens>(this.url("/auth/signup"), { email, password });
  }

  login(email: string, password: string): Promise<AuthTokens> {
    return postJson<AuthTokens>(this.url("/auth/login"), { email, password });
  }

  /** Federated sign-in: trade a Google OIDC ID token for our own token pair. The
   *  server verifies the token and find-or-creates the account. */
  googleSignIn(idToken: string): Promise<AuthTokens> {
    return postJson<AuthTokens>(this.url("/auth/google"), { idToken });
  }

  refresh(refreshToken: string): Promise<AuthTokens> {
    return postJson<AuthTokens>(this.url("/auth/refresh"), { refreshToken });
  }

  logout(refreshToken: string): Promise<void> {
    return postJson<void>(this.url("/auth/logout"), { refreshToken });
  }

  me(accessToken: string): Promise<{ user: AuthUser }> {
    return getJson<{ user: AuthUser }>(this.url("/auth/me"), accessToken);
  }

  /** Permanently delete the authenticated account + all its server-side data. */
  deleteAccount(accessToken: string): Promise<void> {
    return delJson<void>(this.url("/account"), accessToken);
  }

  /** Ask the server to email a password-reset link. Always resolves (the server
   *  replies 204 whether or not the email exists — no enumeration). */
  requestPasswordReset(email: string): Promise<void> {
    return postJson<void>(this.url("/auth/request-password-reset"), { email });
  }

  /** Complete a reset with the token from the email + a new password. */
  resetPassword(token: string, password: string): Promise<void> {
    return postJson<void>(this.url("/auth/reset-password"), { token, password });
  }

  /** Resend the verification email for the signed-in account. */
  resendVerification(accessToken: string): Promise<void> {
    return postJson<void>(this.url("/auth/resend-verification"), {}, { authorization: `Bearer ${accessToken}` });
  }

  private url(path: string): string {
    return trimBase(this.baseUrl) + path;
  }
}
